import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import { movies } from "@/mocks/movies";
import { reviews } from "@/mocks/reviews";
import { genreCategories } from "@/mocks/categories";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9j92s6oi3g3tv8v00g";

const scopeItems = [
  {
    icon: "ri-film-line",
    title: "作品资料",
    description: "收录影片的基本信息、演职员表、剧情简介与题材标签，按统一结构整理。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "影评与笔记",
    description: "以影迷视角撰写的长文影评与观后笔记，保留作者个人观点，不做评分绑架。",
  },
  {
    icon: "ri-filter-3-line",
    title: "筛选与检索",
    description: "支持按题材、年份筛选与排序，并提供全站关键词搜索，快速定位内容。",
  },
];

export default function About() {
  usePageMeta(
    "关于我们 - 苍井空电影大全",
    "了解苍井空电影网站的定位、目标读者、内容范围与整理原则，并可通过意见反馈表单提交资料纠错、作品补充或影评投稿。",
  );
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMessage);
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        formEl.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(
          serverMessage
            ? `提交未成功：${serverMessage}`
            : "提交未成功，请稍后重试或检查填写内容。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络连接出现问题，提交未成功，请稍后重试。");
    }
  };

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1320px] px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_320px] lg:items-end">
            <div>
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-primary-600">
                <span className="h-px w-6 bg-primary-400" />
                About the archive
              </p>
              <h1 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-[36px]">
                关于苍井空电影
              </h1>
              <p className="mt-3 max-w-3xl text-[13px] leading-relaxed text-foreground-600">
                我们是一个由影迷自发维护的影视内容站，专注整理苍井空参与演出的影视作品资料与相关影评。
                站点的目标很简单：让想了解作品的人，能在几步之内找到准确、完整、不夸张的信息。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: `${movies.length}`, label: "作品" },
                { value: `${reviews.length}`, label: "影评" },
                { value: `${genreCategories.length}`, label: "题材" },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 px-3 py-4 text-center">
                  <p className="font-heading text-xl text-primary-700">{item.value}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-[1320px] gap-8 px-4 py-10 md:px-8 md:py-12 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          <section aria-labelledby="position">
            <div className="flex items-center gap-3 border-b border-background-200 pb-3">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              <h2 id="position" className="font-heading text-lg text-foreground-950 md:text-xl">
                网站定位与目标读者
              </h2>
            </div>
            <div className="mt-4 space-y-3.5 text-sm leading-[1.9] text-foreground-700">
              <p>
                本站面向对苍井空影视作品感兴趣的观众，以及希望系统了解其作品脉络的影迷。
                我们不提供在线播放，也不做资源分发，页面上呈现的是作品资料、内容介绍与影评文字。
              </p>
              <p>
                为了兼顾阅读体验与信息密度，作品页采用「资料 + 简介 + 影评摘录 + 相关推荐」的结构，
                分类页则提供题材、年份筛选与排序，让浏览过程更接近翻阅一本整理得当的观影手册。
              </p>
            </div>
          </section>

          <section className="mt-9" aria-labelledby="scope">
            <div className="flex items-center gap-3 border-b border-background-200 pb-3">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              <h2 id="scope" className="font-heading text-lg text-foreground-950 md:text-xl">
                内容范围
              </h2>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {scopeItems.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${item.icon} text-base leading-none`} />
                  </span>
                  <h3 className="mt-3 font-heading text-sm text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-foreground-600">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-9" aria-labelledby="principles">
            <div className="flex items-center gap-3 border-b border-background-200 pb-3">
              <span className="h-4 w-1 rounded-full bg-secondary-500" />
              <h2 id="principles" className="font-heading text-lg text-foreground-950 md:text-xl">
                整理原则与声明
              </h2>
            </div>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
              {[
                "资料以公开信息为基础整理，如与官方信息存在差异，以官方发布为准。",
                "剧情简介尽量避开关键情节，影评摘录会标注来源与作者，不代替完整文章。",
                "站内所有影评均为作者个人观点，不代表本站立场，也不构成观看建议。",
                "分享按钮为演示状态，仅提供复制当前页面链接，不接入真实社交平台接口。",
              ].map((text, index) => (
                <li key={index} className="flex gap-2.5">
                  <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-[10px] text-secondary-700">
                    {index + 1}
                  </span>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </section>

          <section id="feedback" className="mt-10 scroll-mt-24" aria-labelledby="feedback-title">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
              <div className="flex items-center gap-3 border-b border-background-200 pb-3">
                <span className="h-4 w-1 rounded-full bg-primary-500" />
                <h2 id="feedback-title" className="font-heading text-lg text-foreground-950 md:text-xl">
                  意见反馈
                </h2>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                发现资料错误、想补充作品信息，或者希望投稿影评，都可以通过下面的表单告诉我们。
                请尽量写明具体作品与问题，方便我们核对。
              </p>

              {status === "success" ? (
                <div className="mt-5 rounded-md border border-primary-200 bg-primary-50 p-4">
                  <p className="flex items-center gap-2 font-heading text-sm text-primary-800">
                    <i className="ri-checkbox-circle-line text-base" />
                    感谢你的反馈，我们已经收到
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">
                    我们会在整理后核对信息。如需回复，会通过你留下的邮箱联系。
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-3 whitespace-nowrap rounded-md border border-primary-300 px-3 py-1.5 text-xs text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    再提交一条
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="feedback-form"
                  className="mt-5 space-y-4"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-xs text-foreground-600">
                        你的称呼 <span className="text-primary-600">*</span>
                      </span>
                      <input
                        type="text"
                        name="nickname"
                        required
                        maxLength={40}
                        placeholder="怎么称呼你"
                        className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-foreground-600">
                        邮箱 <span className="text-primary-600">*</span>
                      </span>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="用于接收回复"
                        className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-xs text-foreground-600">反馈类型</span>
                    <select
                      name="type"
                      defaultValue="资料纠错"
                      className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                    >
                      <option value="资料纠错">资料纠错</option>
                      <option value="补充作品信息">补充作品信息</option>
                      <option value="影评投稿">影评投稿</option>
                      <option value="站点建议">站点建议</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="flex items-center justify-between text-xs text-foreground-600">
                      <span>
                        反馈内容 <span className="text-primary-600">*</span>
                      </span>
                      <span className="text-[11px] text-foreground-400">{messageLength} / 500</span>
                    </span>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      onChange={(event) => setMessageLength(event.target.value.length)}
                      placeholder="请描述具体问题，例如作品名称与需要更正的信息（最多 500 字）"
                      className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </label>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-extra-field"
                  />

                  {status === "error" && formError ? (
                    <p className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-3 py-2.5 text-xs text-primary-800">
                      <i className="ri-error-warning-line mt-0.5 text-sm" />
                      {formError}
                    </p>
                  ) : null}

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <i className="ri-send-plane-line text-sm" />
                      {status === "submitting" ? "提交中…" : "提交反馈"}
                    </button>
                    <span className="text-[11px] text-foreground-400">
                      提交内容仅用于资料核对，不会公开你的联系方式。
                    </span>
                  </div>
                </form>
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base text-foreground-950">联系与更新</h2>
            <ul className="mt-3 space-y-3 text-xs text-foreground-600">
              <li className="flex items-start gap-2.5">
                <i className="ri-mail-line mt-0.5 text-sm text-primary-500" />
                <span>
                  邮箱
                  <span className="mt-0.5 block text-foreground-800">archive@sora-film.example</span>
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-time-line mt-0.5 text-sm text-primary-500" />
                <span>
                  更新节奏
                  <span className="mt-0.5 block text-foreground-800">每周整理一次资料与影评</span>
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-shield-check-line mt-0.5 text-sm text-primary-500" />
                <span>
                  内容维护
                  <span className="mt-0.5 block text-foreground-800">影迷共建，人工核对后发布</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <p className="flex items-center gap-2 text-[11px] tracking-[0.18em] text-accent-800">
              <i className="ri-information-line text-sm" />
              DISCLAIMER
            </p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              本站为影视资料与影评展示站点，不提供任何影片下载或在线播放服务。
              所有图片均为氛围示意图，仅用于页面展示。
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
            <h2 className="font-heading text-base text-foreground-950">快速入口</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                { label: "电影分类", to: "/category" },
                { label: "影评资讯", to: "/reviews" },
                { label: "全站搜索", to: "/search?q=苍井空" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}